import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import ConfigComponent from '../ConfigComponent/ConfigComponent';
import styles from './MerchantConfigurationsListComponent.module.css';
import {DEFAULT_CURRENCIES} from "../Models/Currencies";
import {getMerchantConfiguration, setMerchantConfiguration} from "../../api/MerchantsApi";
import {DEFAULT_CARDS} from "../Models/Cards";
import {InputTypeEnum} from "../Models/InputTypeEnum";
import {SETTLEMENT_REPORT_TYPE_VALUES} from './MerchantConfigurationValues';

type SectionConfig = {
    section: string;
    configs: Config[];
}

export type Config = {
    label: string;
    configName: string;
    inputType: InputTypeEnum;
    values: { value: string; label: string }[];
    enabled: boolean;
}

const configurations: SectionConfig[] = [
    {
        section: 'Paypal configuration',
        configs: [
            {
                label: 'Multi capture enabled',
                configName: 'IS_PAYPAL_MULTI_CAPTURE',
                inputType: InputTypeEnum.RADIO,
                values: [
                    {value: 'true', label: 'Yes'},
                    {value: 'false', label: 'No'}
                ],
                enabled: true,
            },
            {
                label: 'Paypal settlement flow',
                configName: 'PAYPAL_SETTLEMENT_FLOW',
                inputType: InputTypeEnum.RADIO,
                values: [
                    {value: 'SALE', label: 'SALE (immediate capture)'},
                    {value: 'AUTH', label: 'AUTH (authorization and capture are separate)'}
                ],
                enabled: true,
            }
        ],
    },
    {
        section: 'Credit Card configuration',
        configs: [
            {
                label: 'Multi capture enabled',
                configName: 'IS_CC_MULTI_CAPTURE',
                inputType: InputTypeEnum.RADIO,
                values: [
                    {value: 'true', label: 'Yes'},
                    {value: 'false', label: 'No'}
                ],
                enabled: true,
            },
            {
                label: 'Supported cards',
                configName: 'ALLOWED_CARDS',
                inputType: InputTypeEnum.MULTISELECT,
                values: DEFAULT_CARDS,
                enabled: true
            },
            {
                label: 'Is merchant US acquired?',
                configName: 'IS_US_ACQUIRING_MERCHANT',
                inputType: InputTypeEnum.RADIO,
                values: [
                    {value: 'true', label: 'Yes'},
                    {value: 'false', label: 'No'}
                ],
                enabled: true,
            }
        ],
    },
    {
        section: 'Multi Capture Configuration',
        configs: [
            {
                label: 'Multi capture enabled',
                configName: 'IS_MULTI_CAPTURE',
                inputType: InputTypeEnum.RADIO,
                values: [
                    {value: 'true', label: 'Yes'},
                    {value: 'false', label: 'No'}
                ],
                enabled: true,
            }
        ],
    },
    {
        section: 'Data Retention Configuration',
        configs: [
            {
                label: 'Retention period',
                configName: 'RETENTION_WEEKS',
                inputType: InputTypeEnum.RADIO,
                values: [
                    {value: '4', label: 'Four weeks'},
                    {value: '6', label: 'Six weeks'},
                    {value: '8', label: 'Eight weeks'},
                    {value: '10', label: 'Ten weeks'},
                ],
                enabled: true,
            }
        ],
    },
    {
        section: 'Currency Configuration',
        configs: [
            {
                label: 'Allowed Currencies',
                configName: 'ALLOWED_CURRENCIES',
                inputType: InputTypeEnum.MULTISELECT,
                values: DEFAULT_CURRENCIES,
                enabled: true
            },
            {
                label: 'Default Currency',
                configName: 'DEFAULT_CURRENCY',
                inputType: InputTypeEnum.SELECT,
                values: DEFAULT_CURRENCIES,
                enabled: true,
            }
        ],
    },
    {
        section: 'Payment Advice Configuration',
        configs: [
            {
                label: 'Settlement reports type to be processed',
                configName: 'SETTLEMENT_REPORT_TYPE',
                inputType: InputTypeEnum.RADIO,
                values: SETTLEMENT_REPORT_TYPE_VALUES,
                enabled: true,
            },
            {
                label: 'Should export Payment Advices only with Disbursement Reference?',
                configName: 'EXPORT_PA_ONLY_WITH_DISBURSEMENT_REFERENCE',
                inputType: InputTypeEnum.RADIO,
                values: [
                    {value: 'true', label: 'Yes'},
                    {value: 'false', label: 'No'}
                ],
                enabled: true,
            }
        ]
    }
];

const MerchantConfigurationsListComponent: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const {merchantId} = useParams<{ merchantId: string }>();
    const [currentConfigs, setCurrentConfigs] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchConfigurations = async () => {
            try {
                if (!merchantId) {
                    setError('Merchant ID is missing');
                    return;
                }

                const fetchedConfigs: Record<string, string> = {};
                for (const section of configurations) {
                    for (const config of section.configs) {
                        if (!config.enabled) continue;
                        const mode = await getMerchantConfiguration(merchantId, config.configName);
                        fetchedConfigs[config.configName] = mode || config.values[0].value;
                    }
                }

                setCurrentConfigs(fetchedConfigs);
            } catch (err) {
                console.error('Error fetching configurations:', err);
                setError('Failed to fetch configurations');
            } finally {
                setLoading(false);
            }
        };

        fetchConfigurations();
    }, [merchantId]);

    const handleModeChange = async (configName: string, newMode: string) => {
        try {
            if (!merchantId) {
                alert('Merchant ID is missing');
                return;
            }
            await setMerchantConfiguration({
                merchantId,
                configurationName: configName,
                configurationValue: newMode,
            });

            setCurrentConfigs({
                ...currentConfigs,
                [configName]: newMode,
            });
        } catch (error) {
            alert(error || 'An error occurred while updating the configuration.');
        }
    };

    if (loading) {
        return <p>Loading configurations...</p>;
    }

    if (error) {
        return <p>{error}</p>;
    }

    const handleGoHome = () => {
        navigate('/');
    };

    return (
        <div>
            {location.pathname !== '/' && location.pathname !== '/login' &&
                renderButton("Back", handleGoHome)}
            <h1>Configuration for Merchant: {merchantId}</h1>
            {configurations.map(section => (
                section.configs.some(c => c.enabled) && (
                    <fieldset key={section.section} className={styles.container}>
                        <legend className={styles.sectionTitle}>{section.section}</legend>

                        <div className={styles.rows}>
                            {section.configs.map(cfg => cfg.enabled && (
                                <div key={cfg.configName} className={styles.row}>
                                    <ConfigComponent
                                        config={cfg}
                                        handleModeChange={(newMode) => handleModeChange(cfg.configName, newMode)}
                                        currentValue={currentConfigs[cfg.configName] || ''}
                                    />
                                </div>
                            ))}
                        </div>
                    </fieldset>
                )
            ))}
        </div>
    );
};

const renderButton = (label: string, onClickHandler: () => void) => (
    <button className="btn btn-primary-light" onClick={onClickHandler}>{label}</button>
);

export default MerchantConfigurationsListComponent;
